import { Amplify } from 'aws-amplify';
import { resendSignUpCode, signIn, signOut,  } from 'aws-amplify/auth';
import { confirmSignIn, fetchAuthSession } from 'aws-amplify/auth';
import { associateWebAuthnCredential, listWebAuthnCredentials, deleteWebAuthnCredential } from 'aws-amplify/auth';

class PasswordLessAuthService {
  constructor() {
    Amplify.configure({
      Auth: {
        Cognito : {
            userPoolId: process.env.REACT_APP_USERPOOLID!,
            userPoolClientId: process.env.REACT_APP_CLIENTID!,
        }
      }
    });
  }

  async signIn(email: string) {
    try {
      await signOut();
      Amplify.configure({
        Auth: {
          Cognito : {
              userPoolId: process.env.REACT_APP_USERPOOLID!,
              userPoolClientId: process.env.REACT_APP_CLIENTID!,
          }
        }
      });
      const user = await signIn({'username': email, 'options': {'authFlowType': 'CUSTOM_WITHOUT_SRP'}});
      return user;
    } catch (error) {
      throw error;
    }
  }

  // Start the choice-based USER_AUTH flow (admins only — enabled on the admin app client). We deliberately
  // DON'T set a preferred challenge, so Cognito responds with the first-auth factors available to THIS user.
  // WEB_AUTHN appears only if the admin has a registered passkey — that lets the caller show a passkey prompt
  // only to enrolled admins, and send everyone else straight to today's OTP flow (no passkey pop-up).
  async beginUserAuth(email: string) {
    await signOut();
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: process.env.REACT_APP_USERPOOLID!,
          userPoolClientId: process.env.REACT_APP_CLIENTID!,
        }
      }
    });
    return await signIn({ username: email, options: { authFlowType: 'USER_AUTH' } });
  }

  // Complete sign-in with the passkey — runs the browser WebAuthn (navigator.credentials.get) ceremony.
  async continueWithPasskey() {
    return await confirmSignIn({ challengeResponse: 'WEB_AUTHN' });
  }

  // Passkey enrollment — requires a live Cognito session (the access token authorises the registration,
  // enforced server-side by Cognito). Runs the browser WebAuthn create() ceremony and binds the passkey
  // to the current Cognito user. The admin portal only surfaces this to a proven admin.
  async registerPasskey() {
    return await associateWebAuthnCredential();
  }

  async listPasskeys() {
    return await listWebAuthnCredentials();
  }

  async deletePasskey(credentialId: string) {
    return await deleteWebAuthnCredential({ credentialId });
  }

  async confirmSignIn(code: string, challenge: string) {
    try {
      const user = await confirmSignIn({challengeResponse: code, options : {'authFlowType': challenge}});
      return user;
    } catch (error) {
      console.error('Error confirming sign in:', error);
      throw error;
    }
  }

  async getTokens() {
    try {
      const session = await fetchAuthSession();
      return { idToken: session.tokens?.idToken?.toString(), accessToken: session.tokens?.accessToken.toString() };
    } catch (error) {
      console.error('Error getting tokens:', error);
      throw error;
    }
  }

  async getCurrentSession() {
    try {
      const session = await fetchAuthSession;
      return session;
    } catch (error) {
      console.error('Error getting current session:', error);
      throw error;
    }
  }

  // async forceRefreshSession(): Promise<any> {
  //   try {
  //     const session = await fetchAuthSession({forceRefresh: true});
  //     const idToken = session.tokens?.idToken?.toString();
  //     return await this.getPracAuthToken(idToken!, sessionStorage.getItem('providerId')!);
  //   } catch (error) {
  //     console.error('Error refreshing session:', error);
  //     throw error;
  //   }
  // }

//   async getPracAuthToken(idToken: string, providerId: string) {
//     try {
//       const response = await fetch(`${environment.practitionerAuthTokenApi}${providerId}`, {
//         headers: { "Authorization": idToken }
//       });
//       const data = await response.json();
//       sessionStorage.setItem('pracAuthToken', data['pracAuthToken']);
//       return data;
//     } catch (error) {
//       console.error('Error while fetching practitioner auth token', error);
//       throw error;
//     }
//   }

  async signOut() {
    try {
      await signOut({ global: true });
    } catch (error) {
      throw error;
    }
  }

  async resendSignUpCode(email: string, challenge: string) {
    try {
      await resendSignUpCode({username: email, options: { clientMetadata: { 'email': email, 'challengeName': challenge } }});
    } catch (error) {
      console.error('Error resending sign up code:', error);
      throw error;
    }
  }
}

export default PasswordLessAuthService;
